import { useState, useCallback } from "react";
import { useLocalParticipant } from "@livekit/components-react";
import { Track } from "livekit-client";
import { Mic, MicOff, Video, VideoOff, SwitchCamera, LogOut } from "lucide-react";

/**
 * LiveRoomAvControls — mic + camera toggles + camera-flip for the
 * host/co-host. Rendered INSIDE the <LiveKitRoom> so `useLocalParticipant()`
 * has a room context.
 *
 * The buttons are optimistic — LiveKit acknowledges the setEnabled call
 * before the local media track actually stops, but the UI flips immediately
 * so it feels responsive. If the underlying call errors, the next render
 * pulls the truth from `isMicrophoneEnabled` / `isCameraEnabled`.
 *
 * Camera flip uses `restartTrack({facingMode})` — the standard LiveKit
 * pattern for switching front/rear cameras without restarting the whole
 * connection. Silently no-ops on desktops with a single webcam.
 */
export default function LiveRoomAvControls({ canPublish, isHost, onLeaveBox }) {
  const { localParticipant, isMicrophoneEnabled, isCameraEnabled } = useLocalParticipant();
  const [facing, setFacing] = useState("user"); // "user" (selfie) | "environment" (rear)
  const [flipping, setFlipping] = useState(false);

  const toggleMic = useCallback(async () => {
    try { await localParticipant.setMicrophoneEnabled(!isMicrophoneEnabled); }
    catch { /* LiveKit will surface the error via its own logger */ }
  }, [localParticipant, isMicrophoneEnabled]);

  const toggleCam = useCallback(async () => {
    try { await localParticipant.setCameraEnabled(!isCameraEnabled); }
    catch { /* silent */ }
  }, [localParticipant, isCameraEnabled]);

  const flipCamera = useCallback(async () => {
    if (!localParticipant || flipping) return;
    const next = facing === "user" ? "environment" : "user";
    setFlipping(true);
    try {
      const pub = localParticipant.getTrackPublication(Track.Source.Camera);
      const track = pub?.track;
      if (track?.restartTrack) {
        await track.restartTrack({ facingMode: next });
      } else {
        // Fallback: cycle the camera track. Silent-noop on single-cam devices.
        await localParticipant.setCameraEnabled(false);
        await localParticipant.setCameraEnabled(true, { facingMode: next });
      }
      setFacing(next);
    } catch {
      /* single-camera desktop or permission denial — silent */
    } finally {
      setFlipping(false);
    }
  }, [localParticipant, facing, flipping]);

  if (!canPublish || !localParticipant) return null;

  // Iter60 — moved out of the chat lane. Previously anchored `left-3 bottom-24`
  // which overlapped LiveChatOverlay's feed + composer. Now docked to the
  // right side ABOVE the LiveRoomActionRail (which sits at right-3 bottom-20).
  // Horizontal row keeps three buttons compact and off the chat lane on 390px.
  //
  // Slice B (Live UI cleanup) — for the HOST, the entire dock is hidden:
  //   * Mic portalled to LiveRoomTopBar (via <HostTopBarMic>, Slice A)
  //   * Camera on/off + Flip portalled into the wrench (via <HostCamControls>, Slice B)
  // Cohosts (canPublish && !isHost) still see the full Mic + Camera + Flip
  // dock so they can manage their own AV without hunting for controls.
  if (isHost) return null;

  return (
    <div className="absolute right-3 bottom-52 z-30 flex flex-row items-center gap-2 pointer-events-auto">
      <button
        type="button"
        data-testid="live-mic-toggle"
        onClick={toggleMic}
        title={isMicrophoneEnabled ? "Mute microphone" : "Unmute microphone"}
        aria-pressed={!isMicrophoneEnabled}
        className={`w-10 h-10 rounded-full backdrop-blur-md border flex items-center justify-center transition-colors ${
          isMicrophoneEnabled
            ? "bg-black/45 border-white/10 text-white hover:border-white/30"
            : "bg-rose-600 border-rose-500 text-white shadow-lg shadow-rose-500/30"
        }`}
      >
        {isMicrophoneEnabled ? <Mic size={16} /> : <MicOff size={16} />}
      </button>
      <button
        type="button"
        data-testid="live-camera-toggle"
        onClick={toggleCam}
        title={isCameraEnabled ? "Turn camera off" : "Turn camera on"}
        aria-pressed={!isCameraEnabled}
        className={`w-10 h-10 rounded-full backdrop-blur-md border flex items-center justify-center transition-colors ${
          isCameraEnabled
            ? "bg-black/45 border-white/10 text-white hover:border-white/30"
            : "bg-rose-600 border-rose-500 text-white shadow-lg shadow-rose-500/30"
        }`}
      >
        {isCameraEnabled ? <Video size={16} /> : <VideoOff size={16} />}
      </button>
      {/* Iter60 — flip camera visible on all viewports (10x10 tile keeps the
          three-button horizontal dock compact on 390px). */}
      <button
        type="button"
        data-testid="live-camera-flip"
        onClick={flipCamera}
        disabled={flipping || !isCameraEnabled}
        title={facing === "user" ? "Switch to rear camera" : "Switch to front camera"}
        className="w-10 h-10 rounded-full backdrop-blur-md border flex items-center justify-center transition-colors bg-black/45 border-white/10 text-white hover:border-white/30 disabled:opacity-40"
      >
        <SwitchCamera size={16} />
      </button>
      {onLeaveBox && (
        <button
          type="button"
          data-testid="live-leave-box"
          onClick={onLeaveBox}
          title="Leave the box — you'll need to request to join again"
          className="h-10 px-3 rounded-full backdrop-blur-md border flex items-center gap-1 text-xs font-semibold bg-black/45 border-white/10 text-white hover:border-rose-400 hover:text-rose-300 transition-colors"
        >
          <LogOut size={14} />
          Leave box
        </button>
      )}
    </div>
  );
}
